import { Icon, Spinner } from "@blueprintjs/core"
import { DateRangeInput } from "@blueprintjs/datetime"
import CustomButton from "components/Atoms/CustomButton"
import { useHost } from "global/host.global"
import { useAccountStatementQuery } from "generated/graphql"
import dayjs from "dayjs"
import React, { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import useAxios from "services/axios.service"
import AppToaster from "utils/AppToaster"
import { inr } from "utils/formatters.utils"

const API_DATE = "YYYY-MM-DD"
const SHOW_DATE = "DD MMM YYYY"
const fmt = (d?: string | Date, f = SHOW_DATE) =>
  d ? dayjs(d).format(f) : "--"

type Range = [Date | null, Date | null]

const SummaryCard = ({ label, value, sub, tone, dark, icon }: any) => (
  <div
    className={`rounded-lg border p-4 ${
      dark ? "bg-gray-900 text-white border-gray-900" : "bg-white"
    }`}
    style={{ border: "1px solid #E5E7EB" }}
  >
    <div className="flex justify-between items-start">
      <span className={`text-xs ${dark ? "text-gray-300" : "text-gray-600"}`}>
        {label}
      </span>
      <Icon icon={icon} className={dark ? "text-gray-100" : "text-gray-600"} />
    </div>
    <div className={`text-2xl font-600 my-2 ${tone || ""}`}>{value}</div>
    <div className={`text-xs ${dark ? "text-gray-300" : "text-gray-600"}`}>
      {sub}
    </div>
  </div>
)

const VendorLedger = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const axios = useAxios()
  const { subdomain } = useHost()
  const [range, setRange] = useState<Range>([null, null])
  const [downloading, setDownloading] = useState<"pdf" | "excel" | null>(null)

  const dateFrom = range[0] ? dayjs(range[0]).format(API_DATE) : undefined
  const dateTo = range[1] ? dayjs(range[1]).format(API_DATE) : undefined

  const { data, loading, error } = useAccountStatementQuery({
    variables: { userId: id, dateFrom, dateTo },
    skip: !id,
  })
  const s = data?.accountStatement

  async function download(type: "pdf" | "excel") {
    setDownloading(type)
    try {
      const res = await axios.get(`/reports/account-statement/${id}/${type}/`, {
        params: { date_from: dateFrom, date_to: dateTo },
        responseType: "blob",
        headers: { tenant: subdomain },
      })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement("a")
      a.href = url
      a.download = `account-statement-${id}.${type === "pdf" ? "pdf" : "xlsx"}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e) {
      AppToaster.show({
        message: e?.message || "Failed to download statement",
        intent: "danger",
      })
    } finally {
      setDownloading(null)
    }
  }

  const customerAddress = [
    s?.customer?.address,
    s?.customer?.district,
    s?.customer?.state,
    s?.customer?.pincode,
  ]
    .filter(Boolean)
    .join(", ")

  return (
    <div className="vendor-ledger">
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b">
        <CustomButton icon="arrow-left" minimal onClick={() => navigate(-1)} />
        <h3 className="m-0 flex-1 text-center font-600">
          {s?.customer?.businessName || "Vendor"} - Ledger Details
        </h3>
      </div>

      <div className="p-4 space-y-4 bg-white mx-8 my-4 rounded-lg border">
        <div
          className="bg-white border rounded-lg p-4 flex flex-wrap justify-between items-center gap-3"
          style={{ border: "1px solid #E5E7EB" }}
        >
          <div>
            <h2 className="m-0 font-600">Account Statement</h2>
            <span className="text-xs text-gray-600">
              Vendor/Customer summary &amp; transactions
            </span>
          </div>
          <div className="flex items-center gap-2">
            <DateRangeInput
              allowSingleDayRange
              formatDate={d => dayjs(d).format(SHOW_DATE)}
              parseDate={str => dayjs(str, SHOW_DATE).toDate()}
              value={range}
              onChange={r => setRange([r[0] || null, r[1] || null])}
              startInputProps={{ placeholder: "From" }}
              endInputProps={{ placeholder: "To" }}
              popoverProps={{ minimal: true }}
            />
            <CustomButton
              icon="document"
              text="Download PDF"
              loading={downloading === "pdf"}
              disabled={!id}
              onClick={() => download("pdf")}
            />
            <CustomButton
              icon="th"
              text="Download Excel"
              loading={downloading === "excel"}
              disabled={!id}
              onClick={() => download("excel")}
            />
          </div>
        </div>

        {loading && !s ? (
          <div className="py-10">
            <Spinner />
          </div>
        ) : error || !s ? (
          <div className="bg-white border rounded-lg p-6 text-center text-red-500">
            {error?.message || "Statement not available"}
          </div>
        ) : (
          <>
            <div
              className="bg-white border rounded-lg p-4"
              style={{ border: "1px solid #E5E7EB" }}
            >
              <div className="flex justify-between items-start border-b mb-4">
                <div>
                  <h3 className="m-0 font-600">{s.issuer?.name || "--"}</h3>
                  <span className="text-xs text-gray-600">
                    {s.issuer?.address}
                  </span>
                </div>
                <div
                  className="text-xs border rounded px-3 py-2 bg-slate-100"
                  style={{ border: "1px solid #E5E7EB" }}
                >
                  Statement Date
                  <div className="text-sm font-600">{fmt(s.statementDate)}</div>
                </div>
              </div>
              <hr />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs font-600 text-gray-600 mb-2">
                    CUSTOMER DETAILS
                  </div>
                  {[
                    ["Account No", s.customer?.accountNumber],
                    ["Name", s.customer?.businessName || s.customer?.pocName],
                    ["Address", customerAddress],
                    ["GST No", s.customer?.gstin],
                  ].map(([k, v]) => (
                    <div key={k} className="flex text-sm mb-1">
                      <span className="w-24 text-gray-600">{k}</span>
                      <span>{v || "-"}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-xs font-600 text-gray-600 mb-2">
                    STATEMENT PERIOD
                  </div>
                  <div
                    className="border rounded bg-slate-100 p-3"
                    style={{ border: "1px solid #E5E7EB" }}
                  >
                    <div className="text-xs text-gray-600">Period Covered</div>
                    <div className="text-sm font-600">
                      {fmt(s.dateFrom)} to {fmt(s.dateTo)}
                    </div>
                  </div>
                  <div className="text-xs italic text-gray-600 mt-2">
                    All Currency Amount in {s.currency || "INR"}.
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 rounded-lg">
              <SummaryCard
                label="Opening Balance"
                icon="credit-card"
                value={inr(s.openingBalance)}
                sub={`as on ${fmt(s.dateFrom)}`}
              />
              <SummaryCard
                label="Total Debits"
                icon="arrow-down"
                tone="text-green"
                value={inr(s.totalDebit)}
                sub="Invoices & Charges"
              />
              <SummaryCard
                label="Total Credits"
                icon="arrow-up"
                tone="text-red"
                value={inr(s.totalCredit)}
                sub="Payments & Credit Notes"
              />
              <SummaryCard
                dark
                label="Closing Balance"
                icon="bank-account"
                value={inr(s.closingBalance)}
                sub={`as on ${fmt(s.dateTo)}`}
              />
            </div>

            <div
              className="bg-white border rounded-lg"
              style={{ border: "1px solid #E5E7EB" }}
            >
              <div className="p-4 font-600 border-b">Transaction Details</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      {[
                        "Date",
                        "Doc Number",
                        "Doc Type",
                        "Invoice No",
                        "Taxable Value",
                        "GST Value",
                        "Withholding Tax",
                        "Debit Amount",
                        "Credit Amount",
                        "Closing Balance",
                      ].map(h => (
                        <th
                          key={h}
                          className="px-3 py-2 font-600 whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {!s.transactions?.length && (
                      <tr>
                        <td
                          colSpan={10}
                          className="text-center py-6 text-gray-500"
                        >
                          No transactions in this period
                        </td>
                      </tr>
                    )}
                    {s.transactions?.map((t, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-2 whitespace-nowrap">
                          {fmt(t.date)}
                        </td>
                        <td className="px-3 py-2">{t.docNumber || "-"}</td>
                        <td className="px-3 py-2 text-blue-600">
                          {t.docType || "-"}
                        </td>
                        <td className="px-3 py-2">{t.invoiceNo || "-"}</td>
                        <td className="px-3 py-2">
                          {inr(t.taxableValue ?? 0)}
                        </td>
                        <td className="px-3 py-2">{inr(t.gstValue ?? 0)}</td>
                        <td className="px-3 py-2 text-blue-600">
                          {t.withholdingTax != null
                            ? inr(t.withholdingTax)
                            : "-"}
                        </td>
                        <td className="px-3 py-2 text-green">
                          {inr(t.debitAmount ?? 0)}
                        </td>
                        <td className="px-3 py-2 text-red">
                          {inr(t.creditAmount ?? 0)}
                        </td>
                        <td className="px-3 py-2">
                          {inr(t.closingBalance ?? 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default VendorLedger
